import { backdropClasses } from '@mui/material/Backdrop';
import type { Components, Theme } from '@mui/material/styles';

// ----------------------------------------------------------------------

const MuiDialog: Components<Theme>['MuiDialog'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: {
    /**
     * TODO: Should be removed in MUI next.
     * @see https://github.com/mui/material-ui/issues/43106
     */
    closeAfterTransition: false,
  },
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    // Scoped to the dialog rather than set on MuiBackdrop, so drawers and menus keep their own scrim.
    root: ({ theme }) => ({
      [`& .${backdropClasses.root}`]: {
        backgroundColor: 'rgba(17, 17, 17, 0.45)',
        ...theme.applyStyles('dark', { backgroundColor: 'rgba(0, 0, 0, 0.6)' }),
      },
    }),
    // Top-anchor every dialog so its header stays put as the body height changes, instead of the whole
    // modal re-centring vertically on each content change (e.g. switching type in the policy form).
    container: { alignItems: 'flex-start' },
    paper: ({ ownerState, theme }) => ({
      backgroundColor: theme.vars.palette.background.paper,
      border: `1px solid ${theme.vars.palette.divider}`,
      borderRadius: 14,
      boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
      // Sit a comfortable distance below the top (not flush against it) and cap the height so a tall
      // dialog keeps a bottom gap and scrolls internally rather than running off-screen.
      ...(!ownerState.fullScreen && {
        margin: theme.spacing(2),
        marginTop: theme.spacing(8),
        maxHeight: `calc(100% - ${theme.spacing(10)})`,
      }),
    }),
    paperFullScreen: { borderRadius: 0 },
  },
};

const MuiDialogTitle: Components<Theme>['MuiDialogTitle'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    // The gap under the header is a margin, not padding, so a scrolling body slides under the title
    // rather than being clipped by it.
    root: ({ theme }) => ({
      padding: '24px 26px 0',
      marginBottom: theme.spacing(3),
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
      color: theme.vars.palette.text.primary,
    }),
  },
};

const MuiDialogContent: Components<Theme>['MuiDialogContent'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    // The header already carries the 24px gap under itself, so content that follows one needs no top
    // padding of its own. A dialog with no title still does.
    root: { padding: '26px', '.MuiDialogTitle-root + &': { paddingTop: 0 } },
    dividers: ({ theme }) => ({
      borderTop: 0,
      borderBottomStyle: 'dashed',
      paddingBottom: theme.spacing(3),
    }),
  },
};

const MuiDialogActions: Components<Theme>['MuiDialogActions'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { disableSpacing: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      padding: '0 26px 26px',
      // Gap, not `:not(:first-of-type)` margins: MUI renders a Button with an `href` as an <a>, which counts
      // as the first of ITS type and so lost its left margin, butting it against the preceding <button>.
      gap: theme.spacing(1.5),
    }),
  },
};

// ----------------------------------------------------------------------

export const dialog = {
  MuiDialog,
  MuiDialogTitle,
  MuiDialogContent,
  MuiDialogActions,
};
